// components/portal/BrandGoogleSummary.tsx
//
// The brand's shops on Google Maps taken together (lib/brands/brandGoogleSignals.ts), in the
// card's Overview: the average rating, reviews per shop (the traffic stand-in) and weekly opening
// hours, then the shops ranked by reviews and the spread of their ratings. Read through the kernel
// (loadBrandGoogleSignals) and cached on the brand's runtime overlay, like the News tab.
import React, { useEffect } from 'react';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import type { BrandGoogleSignalsState } from '#types/types';

const SHOWN_SHOPS = 8;
// Store addresses come in from several sources, some of them in capitals ("BAILLEUL").
const cityLabel = (city: string | null) => city && city === city.toUpperCase() ? city.toLowerCase().replace(/(^|[\s-])\p{L}/gu, (m) => m.toUpperCase()) : city;

export const Stat = ({ value, label }: { value: string; label: string }) => (
  <div className="min-w-0 flex-1 rounded-[16px] bg-[#1B1B1B] px-4 py-3">
    <div className="font-medium text-[#ECECEC]" style={{ fontSize: 22, lineHeight: '28px' }}>{value}</div>
    <div className="mt-0.5 text-[#8E8E8E]" style={{ fontSize: 13 }}>{label}</div>
  </div>
);

export const BrandGoogleSummary = ({ brandId, lang }: { brandId: string; lang: string }) => {
  const { kernel } = useKernel();
  const state = useKernelStore((s) => (s.entityKernelState[`Brand:${brandId}`]?.state as { runtime?: { googleSignals?: BrandGoogleSignalsState } } | undefined)?.runtime?.googleSignals);
  useEffect(() => {
    // The kernel skips the call when this brand's reading is already loading or loaded.
    if (brandId) void kernel.current?.loadBrandGoogleSignals(brandId);
  }, [brandId, kernel]);
  const fr = lang === 'fr';
  const locale = fr ? 'fr-FR' : 'en-GB';
  const number = (n: number) => n.toLocaleString(locale);
  const title = <h3 className="font-semibold text-[#ECECEC]" style={{ fontSize: 16, lineHeight: '24px' }}>{fr ? 'Performance des magasins' : 'Store performance'}</h3>;

  if (!state || state.status === 'loading') {
    return (
      <section aria-busy="true">
        {title}
        <div className="skeleton-in" role="status" aria-label={fr ? 'Lecture des magasins' : 'Reading the shops'}>
          <div className="skeleton mt-1.5 h-2.5 w-40 rounded-full opacity-60" />
          <div className="mt-3 flex gap-2">{[0, 1, 2].map((i) => <div key={i} className="skeleton h-[76px] flex-1 rounded-[16px]" />)}</div>
          <div className="mt-5 space-y-3">{['92%', '78%', '70%', '61%'].map((w) => <div key={w} className="skeleton h-2 rounded-full" style={{ width: w }} />)}</div>
        </div>
      </section>
    );
  }
  const signals = state.status === 'loaded' ? state.signals : null;
  if (!signals) return null;
  const shops = signals.shops.slice(0, SHOWN_SHOPS);
  const top = Math.max(1, ...shops.map((s) => s.reviews));
  const band = Math.max(1, ...signals.ratingBands.map((b) => b.shops));
  const updated = new Date(signals.at).toLocaleDateString(locale, { day: 'numeric', month: 'short' });

  return (
    <section aria-label={fr ? 'Performance des magasins' : 'Store performance'}>
      {title}
      <p className="mt-0.5 text-[#8E8E8E]" style={{ fontSize: 13 }}>
        {fr
          ? `${signals.shopsMatched} magasins lus sur ${number(signals.openShops)} · mis à jour le ${updated}`
          : `${signals.shopsMatched} of ${number(signals.openShops)} shops read · updated ${updated}`}
      </p>
      <div className="mt-3 flex gap-2">
        <Stat value={signals.averageRating !== null ? `${signals.averageRating.toLocaleString(locale)} ★` : '–'} label={fr ? 'Note moyenne' : 'Average rating'} />
        <Stat value={signals.medianReviews !== null ? number(signals.medianReviews) : '–'} label={fr ? 'Avis par magasin' : 'Reviews per shop'} />
        <Stat value={signals.medianWeeklyHours !== null ? `${signals.medianWeeklyHours} h` : '–'} label={fr ? 'Ouverture par semaine' : 'Open per week'} />
      </div>
      {shops.length ? (
        <div className="mt-5">
          <div className="text-[#AFAFAF]" style={{ fontSize: 13 }}>{fr ? 'Les magasins les plus fréquentés, par nombre d’avis' : 'Busiest shops, by number of reviews'}</div>
          <div className="mt-2 flex flex-col gap-1.5">
            {shops.map((shop, index) => (
              <div key={`${shop.name}-${index}`} className="flex items-center gap-3">
                <div className="w-[140px] shrink-0 overflow-hidden text-ellipsis whitespace-nowrap text-[#ECECEC]" style={{ fontSize: 13 }} title={shop.name}>{cityLabel(shop.city) || shop.name}</div>
                <div className="h-2 min-w-0 flex-1 rounded-full bg-white/[0.06]">
                  <div className="h-2 rounded-full bg-[#ECECEC]/80" style={{ width: `${Math.max(2, (100 * shop.reviews) / top)}%` }} />
                </div>
                <div className="w-[104px] shrink-0 text-right tabular-nums text-[#AFAFAF]" style={{ fontSize: 13 }}>{number(shop.reviews)} · {shop.rating.toLocaleString(locale)} ★</div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      <div className="mt-5">
        <div className="text-[#AFAFAF]" style={{ fontSize: 13 }}>{fr ? 'Répartition des notes' : 'How the shops are rated'}</div>
        <div className="mt-2 flex h-[72px] items-end gap-2">
          {signals.ratingBands.map((b) => (
            <div key={b.from} className="flex h-full flex-1 flex-col items-center justify-end gap-1">
              <div className="text-[#8E8E8E]" style={{ fontSize: 12 }}>{b.shops || ''}</div>
              <div className="w-full rounded-[6px] bg-[#ECECEC]/70" style={{ height: (44 * b.shops) / band, minHeight: b.shops ? 4 : 1, opacity: b.shops ? 1 : 0.15 }} />
            </div>
          ))}
        </div>
        <div className="mt-1 flex gap-2">
          {signals.ratingBands.map((b) => (
            <div key={b.from} className="flex-1 text-center text-[#8E8E8E]" style={{ fontSize: 12 }}>{b.from === 0 ? `< ${b.to}` : b.to >= 5 ? `${b.from}+` : `${b.from}–${b.to}`}</div>
          ))}
        </div>
      </div>
    </section>
  );
};
